/**
 * The half of WebRTC-HTTP that publishing and playback have in common.
 *
 * WHIP (ingestion) and WHEP (egress) are the same handshake pointed in opposite
 * directions: build a peer connection, POST its SDP offer to a URL as
 * `application/sdp`, apply the answer that comes back, and remember the
 * `Location` so the session can be closed politely later.
 *
 * The two used to be one file because only publishing existed. Playback now
 * needs the identical exchange - same content type, same credentials rule, same
 * trickle-less gathering, same politeness on the way out - and the parts worth
 * sharing are exactly the parts that are easy to get subtly wrong in one place
 * and not the other.
 */

/** A signalling exchange that the far end refused, or that never reached it. */
export class SignalError extends Error {
  constructor(
    message: string,
    /** The HTTP status, or `0` when the request never got an answer. */
    readonly status: number,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = "SignalError";
  }
}

export interface Exchange {
  /** The far end's SDP answer. */
  answer: string;
  /**
   * The session resource to `DELETE` when finished, absolute.
   *
   * Null when the server did not send a `Location`, which is legal and means
   * sign-off is not available rather than that something went wrong.
   */
  resource: string | null;
}

/**
 * Offer, and take the answer.
 *
 * Sets the local description and waits for candidates before sending, because
 * both protocols take a single complete offer rather than trickling.
 */
export async function exchange(
  url: string,
  pc: RTCPeerConnection,
): Promise<Exchange> {
  const offer = await pc.createOffer();

  await pc.setLocalDescription(offer);
  await gathered(pc);

  let response: Response;

  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/sdp" },
      body: pc.localDescription?.sdp ?? "",
      /*
       * `omit`, and it is load-bearing twice over.
       *
       * The URL is the credential here - for publishing it is a secret handed
       * out by our API, and for playback it is public - so neither request has
       * any business carrying the session cookie to a third-party origin.
       *
       * It also stops the browser answering an authentication challenge on our
       * behalf. A `401` with `WWW-Authenticate` on a credentialed fetch makes
       * Chrome raise a login box over the page, which is how a refused publish
       * used to present itself to a host mid-broadcast: a prompt for a username
       * and password that do not exist. A fetch that cannot answer a challenge
       * never provokes one, and the status comes back as an ordinary response
       * that can be explained properly.
       */
      credentials: "omit",
    });
  } catch (cause) {
    throw new SignalError("Could not reach the stream service.", 0, { cause });
  }

  if (!response.ok) {
    throw new SignalError(
      `The stream service answered ${response.status}.`,
      response.status,
    );
  }

  const location = response.headers.get("Location");

  return {
    answer: await response.text(),
    // Relative Locations are legal and both ends have used them, so this is
    // resolved rather than assumed absolute.
    resource: location ? new URL(location, url).toString() : null,
  };
}

/**
 * Close a session at the far end, best effort.
 *
 * Failure here is not worth reporting: the peer connection is already closed on
 * this side, and a server that never hears the `DELETE` drops the session on
 * its own when the transport dies. The cost of a missed sign-off is a timeout
 * somewhere else, not a stuck broadcast here - and telling somebody about a
 * problem they do not have is worse than staying quiet.
 */
export async function signOff(resource: string | null): Promise<void> {
  if (!resource) return;

  try {
    await fetch(resource, { method: "DELETE", credentials: "omit" });
  } catch {
    // Deliberately empty. See above.
  }
}

/**
 * Wait for ICE gathering, or four seconds, whichever comes first.
 *
 * The timeout is there because gathering can stall indefinitely against an
 * unreachable STUN server, and an offer carrying the candidates found so far
 * usually connects anyway - against a publicly reachable endpoint, which both
 * of these are, the host candidate is enough.
 */
export function gathered(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === "complete") return Promise.resolve();

  return new Promise((resolve) => {
    const done = () => {
      if (pc.iceGatheringState !== "complete") return;

      pc.removeEventListener("icegatheringstatechange", done);
      clearTimeout(timer);
      resolve();
    };

    const timer = setTimeout(() => {
      pc.removeEventListener("icegatheringstatechange", done);
      resolve();
    }, 4000);

    pc.addEventListener("icegatheringstatechange", done);
  });
}
